import React, { FC, ButtonHTMLAttributes, ComponentProps } from 'react';
import styled from 'styled-components';

import { Spinner } from '../Spinner';

import * as buttonComponents from './ButtonVariants';

const buttonVariants = {
  primary: buttonComponents.PrimaryButton,
  secondary: buttonComponents.SecondaryButton,
  tertiary: buttonComponents.TertiaryButton,
  text: buttonComponents.TextButton,
};

type BaseButtonProps = ComponentProps<typeof buttonComponents.ButtonWrapper> &
  ButtonHTMLAttributes<HTMLButtonElement>;
type SpinnerProps = ComponentProps<typeof Spinner>;

type ButtonProps = {
  testId?: string;
  'data-test-id'?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'text';
  spinnerProps?: SpinnerProps;
} & BaseButtonProps;

const Button: FC<ButtonProps> = ({
  testId,
  'data-test-id': dataTestId,
  isLoading,
  isDisabled,
  disabled,
  children,
  variant = 'primary',
  type = 'button',
  spinnerProps,
  ...props
}) => {
  const ButtonComponent = buttonVariants[variant];
  const buttonSpinnerProps: SpinnerProps = {
    isCentered: true,
    isInverted: !props.isInverted,
    size: 'small',
    ...spinnerProps,
  };

  return (
    <ButtonComponent
      {...props}
      data-test-id={dataTestId || testId}
      disabled={isDisabled || disabled || isLoading}
      type={type}
    >
      {isLoading ? <StyledSpinner {...buttonSpinnerProps} /> : children}
    </ButtonComponent>
  );
};

type SemanticButtonProps = Omit<ButtonProps, 'variant'>;

const PrimaryButton: FC<ButtonProps> = (props) => (
  <Button {...props} variant="primary" />
);

const SecondaryButton: FC<SemanticButtonProps> = (props) => (
  <Button {...props} variant="secondary" />
);

const TertiaryButton: FC<SemanticButtonProps> = (props) => (
  <Button {...props} variant="tertiary" />
);

const TextButton: FC<ButtonProps> = (props) => (
  <Button {...props} variant="text" />
);

const StyledSpinner = styled(Spinner)`
  /* Theming Overrides */
  ${({ theme }) =>
    theme?.overrides?.Button?.Spinner || theme?.overrides?.Spinner}
`;

export { Button, PrimaryButton, SecondaryButton, TertiaryButton, TextButton };
